import { useEffect, useLayoutEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { stripLanguagePrefix, useLanguage } from '../contexts/LanguageContext';
import { removeStructuredData, setStructuredData, siteOrigin, updatePageSeo } from '../utils/seo';
import { searchSeo } from '../utils/searchSeo';

const PUBLIC_PAGES = {
  '/': {
    title: 'Eulua | Connecting the World',
    description: 'Discover business products from verified suppliers, compare commercial terms, and buy with shipping, taxes, and delivery dates confirmed before payment.',
  },
  '/search': {
    title: 'Browse Products and Suppliers | Eulua Marketplace',
    description: 'Search products from verified suppliers and compare pricing, minimum quantities, location, delivery, and pickup options on Eulua.',
  },
  '/about': {
    title: 'About Eulua | A Clearer Way to Buy and Sell',
    description: 'Learn how Eulua connects business buyers and verified suppliers through transparent product, delivery, tax, and payment workflows.',
  },
  '/sell': {
    title: 'Sell Internationally with Eulua | Free Seller Registration',
    description: 'Join Eulua as a seller for free and compare Starter, Lite, Growth, and Enterprise, with unlimited inventory and no sales commission.',
  },
  '/shipping-policy': {
    title: 'Shipping and Pickup Policy | Eulua',
    description: 'Review how Eulua suppliers confirm shipping costs, pickup options, delivery dates, and fulfillment responsibilities before payment.',
  },
  '/payment-methods': {
    title: 'Secure Payment Methods | Eulua',
    description: 'Understand Eulua payment methods, authorized supplier bank accounts, transfer receipts, review steps, and payment security guidance.',
  },
  '/refunds': {
    title: 'Returns and Refunds | Eulua Buyer Support',
    description: 'Learn how to request a return or refund on Eulua, including eligibility, review steps, timelines, and buyer responsibilities.',
  },
  '/privacy': {
    title: 'Privacy Policy | Eulua Marketplace',
    description: 'Read how Eulua collects, uses, protects, and shares account, location, marketplace, and transaction information.',
  },
  '/cookies': {
    title: 'Cookie Policy | Eulua Marketplace',
    description: 'Learn how Eulua uses necessary browser storage, language, currency and location preferences, and optional personalization technologies.',
  },
  '/terms': {
    title: 'Terms and Conditions | Eulua Marketplace',
    description: 'Review the terms that govern buyer accounts, supplier listings, orders, payments, fulfillment, and marketplace use on Eulua.',
  },
  '/developers': {
    title: 'Eulua API for Developers | Enterprise Integrations',
    description: 'Learn what the Eulua Enterprise API offers: catalog, inventory, orders, quotes, analytics and billing endpoints, authentication, security, and rate limits.',
  },
};

const PRIVATE_PAGES = {
  '/login': ['Sign In | Eulua', 'Sign in to manage your Eulua account, orders, saved products, and supplier activity.'],
  '/forgot-password': ['Reset Your Password | Eulua', 'Request a secure password reset link for your Eulua account.'],
  '/reset-password': ['Choose a New Password | Eulua', 'Create a new secure password for your Eulua account.'],
  '/verify-email': ['Verify Your Email | Eulua', 'Verify your email address to finish setting up your Eulua account.'],
  '/cart': ['Your Cart | Eulua', 'Review products, quantities, delivery details, and supplier terms before submitting your Eulua order request.'],
  '/account': ['My Account | Eulua', 'Manage your Eulua account details, saved products, and recent activity.'],
  '/finances': ['Finances | Eulua', 'Review your private monthly invoices for purchases, sales, memberships, advertising, and commissions.'],
  '/browsing-history': ['Browsing History | Eulua', 'Review products you recently viewed on Eulua.'],
  '/watchlist': ['Saved Products | Eulua', 'View and manage products saved to your Eulua watchlist.'],
  '/orders': ['My Orders | Eulua', 'Track requests, supplier confirmations, payments, delivery, and pickup for your Eulua orders.'],
  '/sell/dashboard': ['Supplier Dashboard | Eulua', 'Manage your Eulua supplier profile, product catalog, orders, and account activity.'],
  '/sell/register': ['Become a Supplier | Eulua', 'Submit your business details to apply as a verified supplier on Eulua.'],
  '/sell/product': ['Publish a Product | Eulua', 'Create a new product listing for your Eulua supplier catalog.'],
  '/sell/products': ['Supplier Product Catalog | Eulua', 'Manage the product listings in your Eulua supplier catalog.'],
  '/sell/orders': ['Supplier Orders | Eulua', 'Review and manage customer orders received through Eulua.'],
  '/thank-you': ['Thank You | Eulua', 'Your Eulua submission has been received. Review what happens next and continue managing your order.'],
};

const PUBLIC_PAGES_ES = {
  '/': {
    title: 'Eulua | Conectando el mundo',
    description: 'Descubre productos empresariales de proveedores verificados, compara condiciones comerciales y compra con envío, impuestos y fechas de entrega confirmados antes del pago.',
  },
  '/search': {
    title: 'Explora productos y proveedores | Mercado Eulua',
    description: 'Busca productos de proveedores verificados y compara precios, cantidades mínimas, ubicación, entrega y opciones de recogida en Eulua.',
  },
  '/about': {
    title: 'Acerca de Eulua | Una forma más clara de comprar y vender',
    description: 'Conoce cómo Eulua conecta compradores empresariales y proveedores verificados mediante procesos transparentes de productos, entrega, impuestos y pagos.',
  },
  '/sell': {
    title: 'Vende internacionalmente con Eulua | Registro gratuito',
    description: 'Únete gratis a Eulua como vendedor y compara Starter, Lite, Growth y Enterprise, con inventario ilimitado y sin comisión de venta.',
  },
  '/shipping-policy': {
    title: 'Política de envíos y recogida | Eulua',
    description: 'Consulta cómo los proveedores de Eulua confirman costos de envío, recogida, fechas de entrega y responsabilidades de cumplimiento antes del pago.',
  },
  '/payment-methods': {
    title: 'Transferencias bancarias seguras | Eulua',
    description: 'Conoce las cuentas bancarias autorizadas de proveedores, los comprobantes de transferencia, los pasos de revisión y la seguridad de pagos en Eulua.',
  },
  '/refunds': {
    title: 'Devoluciones y reembolsos | Soporte de Eulua',
    description: 'Conoce cómo solicitar una devolución o reembolso en Eulua, incluidos los requisitos, pasos de revisión, plazos y responsabilidades del comprador.',
  },
  '/privacy': {
    title: 'Política de privacidad | Mercado Eulua',
    description: 'Consulta cómo Eulua recopila, usa, protege y comparte información de cuentas, ubicación, mercado y transacciones.',
  },
  '/cookies': {
    title: 'Política de cookies | Mercado Eulua',
    description: 'Conoce cómo Eulua usa almacenamiento necesario del navegador, preferencias de idioma, moneda y ubicación y tecnologías opcionales de personalización.',
  },
  '/terms': {
    title: 'Términos y condiciones | Mercado Eulua',
    description: 'Consulta los términos que regulan cuentas de compradores, publicaciones de proveedores, pedidos, pagos, cumplimiento y uso del mercado Eulua.',
  },
  '/developers': {
    title: 'API de Eulua para desarrolladores | Integraciones Enterprise',
    description: 'Conoce lo que ofrece la API Enterprise de Eulua: catálogo, inventario, pedidos, cotizaciones, analítica y facturación, autenticación, seguridad y límites de uso.',
  },
};

const PRIVATE_PAGES_ES = {
  '/login': ['Iniciar sesión | Eulua', 'Inicia sesión para gestionar tu cuenta, pedidos, productos guardados y actividad de proveedor en Eulua.'],
  '/forgot-password': ['Restablecer contraseña | Eulua', 'Solicita un enlace seguro para restablecer la contraseña de tu cuenta de Eulua.'],
  '/reset-password': ['Crear una contraseña nueva | Eulua', 'Crea una contraseña nueva y segura para tu cuenta de Eulua.'],
  '/verify-email': ['Verificar correo electrónico | Eulua', 'Verifica tu correo electrónico para completar la configuración de tu cuenta de Eulua.'],
  '/cart': ['Tu carrito | Eulua', 'Revisa productos, cantidades, entrega y condiciones del proveedor antes de enviar tu solicitud de pedido en Eulua.'],
  '/account': ['Mi cuenta | Eulua', 'Gestiona los datos de tu cuenta, productos guardados y actividad reciente en Eulua.'],
  '/finances': ['Finanzas | Eulua', 'Consulta tus facturas mensuales privadas de compras, ventas, membresías, publicidad y comisiones.'],
  '/browsing-history': ['Historial de navegación | Eulua', 'Revisa los productos que viste recientemente en Eulua.'],
  '/watchlist': ['Productos guardados | Eulua', 'Consulta y gestiona los productos guardados en tus favoritos de Eulua.'],
  '/orders': ['Mis pedidos | Eulua', 'Sigue solicitudes, confirmaciones de proveedores, pagos, entregas y recogidas de tus pedidos en Eulua.'],
  '/sell/dashboard': ['Panel de vendedor | Eulua', 'Gestiona tu perfil de proveedor, catálogo de productos, pedidos y actividad de la cuenta en Eulua.'],
  '/sell/register': ['Conviértete en proveedor | Eulua', 'Envía los datos de tu empresa para solicitar la verificación como proveedor en Eulua.'],
  '/sell/product': ['Publicar un producto | Eulua', 'Crea una publicación nueva para tu catálogo de proveedor en Eulua.'],
  '/sell/products': ['Catálogo de productos del proveedor | Eulua', 'Gestiona las publicaciones de productos de tu catálogo de proveedor en Eulua.'],
  '/sell/orders': ['Pedidos del proveedor | Eulua', 'Revisa y gestiona los pedidos de clientes recibidos mediante Eulua.'],
  '/thank-you': ['Gracias | Eulua', 'Eulua recibió tu envío. Consulta los próximos pasos y continúa gestionando tu pedido.'],
};

// GA4's built-in site-search measurement reads the search term off
// page_location, so /search keeps an explicit allowlist of the harmless,
// content-defining parameters. Everything else is dropped: routes such as
// /reset-password?token= and /verify-email?token= carry one-time secrets that
// must never leave the app, let alone land in a third-party analytics dataset.
const ANALYTICS_QUERY_KEYS = ['q', 'sector', 'category', 'subcategory', 'brands'];

const analyticsQuery = (basePath, search) => {
  if (basePath !== '/search' || !search) return '';
  const incoming = new URLSearchParams(search);
  const kept = new URLSearchParams();
  ANALYTICS_QUERY_KEYS.forEach((key) => {
    const value = incoming.get(key);
    if (value) kept.set(key, value);
  });
  const query = kept.toString();
  return query ? `?${query}` : '';
};

const getPageMeta = (pathname, language = 'en') => {
  const publicPages = language === 'es' ? PUBLIC_PAGES_ES : PUBLIC_PAGES;
  const privatePages = language === 'es' ? PRIVATE_PAGES_ES : PRIVATE_PAGES;
  if (publicPages[pathname]) return { ...publicPages[pathname], noindex: false };
  if (privatePages[pathname]) {
    const [title, description] = privatePages[pathname];
    return { title, description, noindex: true };
  }

  const productMatch = pathname.match(/^\/products\/([^/]+)$/);
  if (productMatch) return {
    title: language === 'es' ? 'Producto | Mercado Eulua' : 'Product | Eulua Marketplace',
    description: language === 'es' ? 'Revisa especificaciones, proveedor, precios, cantidades, estimaciones de envío y opciones de recogida del producto en Eulua.' : 'Review product specifications, supplier information, pricing, quantities, shipping estimates, and pickup options on Eulua.',
    noindex: false,
  };

  const sellerReviewsMatch = pathname.match(/^\/sellers\/([^/]+)\/reviews$/);
  if (sellerReviewsMatch) return {
    title: language === 'es' ? 'Reseñas verificadas del vendedor | Mercado Eulua' : 'Verified Seller Reviews | Eulua Marketplace',
    description: language === 'es' ? 'Lee reseñas paginadas de compras verificadas completadas con este vendedor de Eulua.' : 'Read paginated reviews from verified purchases completed with this Eulua seller.',
    noindex: false,
  };

  const sellerMatch = pathname.match(/^\/sellers\/([^/]+)$/);
  if (sellerMatch) return {
    title: language === 'es' ? 'Perfil del vendedor | Mercado Eulua' : 'Seller Profile | Eulua Marketplace',
    description: language === 'es' ? 'Explora el perfil verificado, la información empresarial, la ubicación y el catálogo disponible de este proveedor de Eulua.' : 'Explore this verified Eulua supplier profile, business information, location, and available product catalog.',
    noindex: false,
  };

  const quoteRequestMatch = pathname.match(/^\/orders\/requests\/([^/]+)$/);
  if (quoteRequestMatch) return language === 'es'
    ? { title: 'Solicitud de cotización | Eulua', description: 'Revisa las cotizaciones recibidas de los proveedores y acepta la mejor oferta para tu solicitud en Eulua.', noindex: true }
    : { title: 'Quote Request | Eulua', description: 'Review supplier quotes and accept the best offer for your request on Eulua.', noindex: true };

  const orderMatch = pathname.match(/^\/orders\/([^/]+)$/);
  if (orderMatch) return language === 'es'
    ? { title: 'Detalles del pedido | Eulua', description: 'Revisa respuestas de proveedores, estado del pago, cumplimiento y próximos pasos de tu pedido en Eulua.', noindex: true }
    : { title: 'Order Details | Eulua', description: 'Review supplier responses, payment status, fulfillment details, and next steps for your Eulua order.', noindex: true };
  const checkoutMatch = pathname.match(/^\/checkout\/([^/]+)$/);
  if (checkoutMatch) return language === 'es'
    ? { title: 'Pago seguro del pedido | Eulua', description: 'Envía de forma segura transferencias bancarias y comprobantes para que los proveedores los revisen en Eulua.', noindex: true }
    : { title: 'Secure Order Checkout | Eulua', description: 'Submit supplier bank transfers and receipts securely for review on Eulua.', noindex: true };
  const editProductMatch = pathname.match(/^\/sell\/product\/edit\/([^/]+)$/);
  if (editProductMatch) return language === 'es'
    ? { title: 'Editar producto | Eulua', description: 'Actualiza esta publicación en tu catálogo protegido de proveedor de Eulua.', noindex: true }
    : { title: 'Edit Product | Eulua', description: 'Update this product listing in your protected Eulua supplier catalog.', noindex: true };
  const sellerOrderMatch = pathname.match(/^\/sell\/orders\/([^/]+)$/);
  if (sellerOrderMatch) return language === 'es'
    ? { title: 'Detalles del pedido del proveedor | Eulua', description: 'Revisa y gestiona este pedido de cliente en tu cuenta de proveedor de Eulua.', noindex: true }
    : { title: 'Supplier Order Details | Eulua', description: 'Review and manage this customer order in your Eulua supplier account.', noindex: true };
  const sellQuoteMatch = pathname.match(/^\/sell\/quotes\/([^/]+)$/);
  if (sellQuoteMatch) return language === 'es'
    ? { title: 'Detalles de la solicitud de cotización | Eulua', description: 'Revisa y responde esta solicitud de cotización de comprador en tu cuenta de proveedor de Eulua.', noindex: true }
    : { title: 'Quote Request Details | Eulua', description: 'Review and respond to this buyer quote request in your Eulua supplier account.', noindex: true };
  if (/^\/sell\//.test(pathname)) return language === 'es'
    ? { title: 'Herramientas del proveedor | Eulua', description: 'Gestiona tu flujo protegido de proveedor en Eulua.', noindex: true }
    : { title: 'Supplier Tools | Eulua', description: 'Manage your protected supplier workflow on Eulua.', noindex: true };
  if (/^\/admin\/orders\/[^/]+$/.test(pathname)) return { title: 'Order Administration | Eulua', description: 'Protected Eulua order administration area.', noindex: true };
  if (/^\/admin/.test(pathname)) {
    const area = pathname.split('/').filter(Boolean).slice(1).join(' ').replace(/-/g, ' ');
    const label = area ? area.replace(/\b\w/g, (letter) => letter.toUpperCase()) : 'Dashboard';
    return { title: `${label} | Eulua Administration`, description: 'Protected Eulua administration area.', noindex: true };
  }

  return language === 'es'
    ? { title: 'Página no encontrada | Eulua', description: 'No se encontró la página solicitada. Explora productos y proveedores verificados en Eulua.', noindex: true }
    : { title: 'Page Not Found | Eulua', description: 'The requested page could not be found. Browse products and verified suppliers on Eulua.', noindex: true };
};

const SeoManager = () => {
  const { pathname, search } = useLocation();
  const { language, t } = useLanguage();
  const pageTitleRef = useRef('');
  const lastPageViewRef = useRef('');
  const lastSeoRouteRef = useRef('');

  useLayoutEffect(() => {
    const basePath = stripLanguagePrefix(pathname);
    const isEntityDetail = /^\/(products|sellers)\/[^/]+$/.test(basePath);
    const routeKey = `${language}:${pathname}:${isEntityDetail ? '' : search}`;
    // Tracking parameters do not change an entity page. Preserve its loaded
    // metadata and JSON-LD when only those parameters change.
    if (lastSeoRouteRef.current === routeKey) return;
    lastSeoRouteRef.current = routeKey;
    const isSpanishUrl = basePath !== pathname;
    const withEs = (path) => (path === '/' ? '/es' : `/es${path}`);
    let meta = getPageMeta(basePath, language);
    let canonicalBase = basePath === '/' ? '/' : basePath.replace(/\/$/, '');
    if (basePath === '/search') {
      const searchMeta = searchSeo(search);
      const label = searchMeta.label ? t(searchMeta.label) : '';
      canonicalBase = searchMeta.canonicalBase;
      meta = { ...meta, noindex: searchMeta.noindex, nofollow: false };
      if (label) {
        meta = {
          ...meta,
          title: language === 'es' ? `Productos de ${label} | Mercado Eulua` : `${label} Products | Eulua Marketplace`,
          description: language === 'es' ? `Explora productos de ${label}, proveedores, precios y disponibilidad en el Mercado Eulua.` : `Browse ${label} products, suppliers, pricing and availability on Eulua Marketplace.`,
        };
      }
    }
    if (basePath === '/sell/dashboard' && new URLSearchParams(search).get('tab') === 'advertising') {
      meta = language === 'es'
        ? { title: 'Publicidad para vendedores | Eulua', description: 'Crea y gestiona campañas publicitarias para tus productos y tu perfil de proveedor en Eulua.', noindex: true }
        : { title: 'Seller Advertising | Eulua', description: 'Create and manage advertising campaigns for your products and supplier profile on Eulua.', noindex: true };
    }
    const origin = siteOrigin();
    updatePageSeo({
      ...meta,
      canonicalPath: isSpanishUrl ? withEs(canonicalBase) : canonicalBase,
      alternateEnPath: canonicalBase,
      alternateEsPath: withEs(canonicalBase),
      language,
    });
    pageTitleRef.current = meta.title;
    removeStructuredData('eulua-product-structured-data');
    removeStructuredData('eulua-seller-structured-data');

    if (basePath !== '/') {
      removeStructuredData('eulua-structured-data');
      return;
    }
    setStructuredData('eulua-structured-data', {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'Organization',
          '@id': `${origin}/#business`,
          name: 'Eulua',
          url: `${origin}/`,
          // Google requires an Organization logo of at least 112x112 px.
          logo: `${origin}/android-chrome-512x512.png`,
          image: `${origin}/og-image.png`,
          description: (language === 'es' ? PUBLIC_PAGES_ES : PUBLIC_PAGES)['/'].description,
          email: 'jm@eulua.com',
          areaServed: language === 'es' ? 'Todo el mundo' : 'Worldwide',
        },
        {
          '@type': 'WebSite',
          '@id': `${origin}/#website`,
          url: `${origin}/`,
          name: 'Eulua',
          publisher: { '@id': `${origin}/#business` },
          potentialAction: {
            '@type': 'SearchAction',
            target: `${origin}/search?q={search_term_string}`,
            'query-input': 'required name=search_term_string',
          },
        },
        {
          '@type': 'FAQPage',
          mainEntity: (language === 'es' ? [
            ['¿Qué es Eulua?', 'Eulua es un mercado B2B que conecta compradores empresariales con proveedores verificados y mantiene visibles los datos de productos, entrega, impuestos y pagos durante el pedido.'],
            ['¿Cómo funciona la compra a varios proveedores?', 'Puedes solicitar productos de varios proveedores en un pedido. Cada proveedor confirma de forma independiente inventario, envío o recogida, impuestos y fecha de entrega antes del pago.'],
            ['¿Cuándo pago un pedido?', 'El pago se habilita después de que un proveedor acepta su parte del pedido y confirma las condiciones comerciales finales.'],
            ['¿Puedo recoger un pedido con el proveedor?', 'Sí, cuando la recogida está disponible. La ciudad y el país del proveedor aparecen antes del pago y los datos exactos se muestran después de confirmar el pago.'],
            ['¿Cómo puede vender mi empresa en Eulua?', 'Crea una cuenta, envía la solicitud de proveedor y facilita los datos empresariales y de identidad solicitados para la verificación.'],
          ] : [
            ['What is Eulua?', 'Eulua is a B2B marketplace that connects business buyers with verified suppliers and keeps product, delivery, tax, and payment details visible throughout the order process.'],
            ['How does buying from multiple suppliers work?', 'You can request products from several suppliers in one order. Each supplier independently confirms inventory, shipping or pickup, taxes, and delivery timing before payment.'],
            ['When do I pay for an order?', 'Payment becomes available after a supplier accepts its part of the order and confirms the final commercial terms.'],
            ['Can I pick up an order from a supplier?', 'Yes, when pickup is available. The supplier city and country are shown before payment, and exact pickup details are released after payment is confirmed.'],
            ['How can my business sell on Eulua?', 'Create an account, submit the supplier application, and provide the requested business and identity details for verification.'],
          ]).map(([name, text]) => ({ '@type': 'Question', name, acceptedAnswer: { '@type': 'Answer', text } })),
        },
      ],
    });
  }, [language, pathname, search, t]);

  // Manual SPA page_view: gtag's automatic pageview is turned off in
  // public/gtag-init.js because it would only fire once, for the document that
  // actually loaded.
  //
  // One navigation can re-run this effect several times before it settles - the
  // /sell -> /es/sell language redirect and the geolocation-based language
  // auto-detection both change pathname/language a tick after mount - and each
  // pass used to send its own page_view, double counting every Spanish visit.
  // Scheduling the send and cancelling it on re-run collapses that burst into a
  // single page_view for the route the visitor actually landed on.
  //
  // The query string is dropped everywhere except the /search allowlist above,
  // so one-time secrets in the URL never reach Analytics.
  useEffect(() => {
    if (typeof window.gtag !== 'function') return undefined;
    const timer = setTimeout(() => {
      const query = analyticsQuery(stripLanguagePrefix(pathname), search);
      const pagePath = `${pathname}${query}`;
      const pageLocation = `${window.location.origin}${pagePath}`;
      // Changing a filter on /search can leave the reported location identical;
      // sending it twice would double count the same pageview.
      if (lastPageViewRef.current === pageLocation) return;
      lastPageViewRef.current = pageLocation;
      window.gtag('event', 'page_view', {
        page_title: pageTitleRef.current,
        page_path: pagePath,
        page_location: pageLocation,
      });
    }, 250);
    return () => clearTimeout(timer);
    // `language` is not a dependency: every language switch also rewrites the
    // path (/sell <-> /es/sell), so pathname already covers it.
  }, [pathname, search]);

  return null;
};

export default SeoManager;
